import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Upload, Trash2, Edit, FileText } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { DocumentUploadModal } from '../production/DocumentUploadModal';
import { DocumentDownloadModal } from '../production/DocumentDownloadModal';
import { bulkDeleteRegistry, deleteRegistryDocumentsByDocId } from '../../../../../redux/Project/registrySaga';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import { AddEquipmentModal } from './AddEquipmentModal';
import { DeleteConfirmationDialog } from '../../../../../components/ui/DeleteConfirmationDialog';
import { useEquipmentFilters } from './Filters/hooks/useEquipmentFilters';
import { EquipmentFilters } from './Filters/EquipmentFilters';
import { formatDateOnly } from '../../../../../lib/formatUtils';
import { fetchEquipmentData, refreshEquipmentData } from '../../../../../redux/Project/registryData/action';

interface EquipmentTableProps {
  equipmentType: string;
  project: DashboardProject;
  onRefresh?: () => void;
}

export const EquipmentTable: React.FC<EquipmentTableProps> = ({ equipmentType, project, onRefresh }) => {
  const dispatch = useDispatch();
  const projectId = project?.project?.projectId;
  
  // Get equipment data from Redux store
  const registryData = useSelector((state: any) => state.registryData);
  const equipmentDataFromStore = useMemo(() => {
    return projectId ? registryData?.equipmentData?.[projectId] : null;
  }, [registryData?.equipmentData, projectId]);
  const equipmentLoading = registryData?.equipmentLoading || false;

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDocumentDeleteModalOpen, setIsDocumentDeleteModalOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<any>(null);
  const [equipmentToEdit, setEquipmentToEdit] = useState<any>(null);
  const [equipmentToDelete, setEquipmentToDelete] = useState<any>(null);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);
  const [equipmentList, setEquipmentList] = useState<any[]>([]);

  // Use the equipment filters hook
  const {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueEquipmentNumbers,
    uniqueEquipmentNames,
    uniqueEquipmentTypes,
    uniqueSerialNumbers,
    filteredEquipment,
    hasActiveFilters,
    filteredCount,
    onResetFilters,
  } = useEquipmentFilters({ equipmentList });

  // Map equipment type to API response key - try multiple formats
  const getEquipmentForType = (response: any) => {
    const normalizeRegistryKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

    // Try different key formats: "Welding", "WELDING", "welding", etc.
    const possibleKeys = [
      equipmentType.charAt(0).toUpperCase() + equipmentType.slice(1), // "Welding"
      equipmentType.toUpperCase(), // "WELDING"
      equipmentType.toLowerCase(), // "welding"
      equipmentType, // original
    ];

    for (const key of possibleKeys) {
      if (response[key] && Array.isArray(response[key])) {
        return response[key];
      }
    }

    const normalizedEquipmentType = normalizeRegistryKey(equipmentType);
    const matchingKey = Object.keys(response).find((key) => normalizeRegistryKey(key) === normalizedEquipmentType);
    if (matchingKey && Array.isArray(response[matchingKey])) {
      return response[matchingKey];
    }

    // If no match found, return empty array
    return [];
  };

  // Fetch equipment data from Redux store or dispatch action
  useEffect(() => {
    if (!projectId) return;

    if (equipmentDataFromStore) {
      // Extract equipment for current type from Redux store
      const equipment = getEquipmentForType(equipmentDataFromStore);
      setEquipmentList(equipment);
    } else if (!equipmentLoading) {
      // Fetch from API if not in store and not currently loading
      dispatch(fetchEquipmentData({ projectId }));
    }
  }, [equipmentDataFromStore, equipmentType, projectId, equipmentLoading, dispatch]);

  // Update equipment list when store data changes
  useEffect(() => {
    if (equipmentDataFromStore) {
      const equipment = getEquipmentForType(equipmentDataFromStore);
      setEquipmentList(equipment);
    }
  }, [equipmentDataFromStore, equipmentType]);

  const handleUpload = (equipment: any) => {
    setSelectedEquipment(equipment);
    setIsUploadModalOpen(true);
  };

  const handleDocumentClick = (equipment: any) => {
    setSelectedEquipment(equipment);
    setIsDownloadModalOpen(true);
  };

  const handleEdit = (equipment: any) => {
    setEquipmentToEdit(equipment);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (equipment: any) => {
    setEquipmentToDelete(equipment);
    setIsDeleteModalVisible(true);
  };

  const handleUploadComplete = async (response: any) => {
    if (response && selectedEquipment) {
      // Refresh Redux store data
      if (projectId) {
        dispatch(refreshEquipmentData({ projectId }));
      }
      if (onRefresh) {
        onRefresh();
      }
      setIsUploadModalOpen(false);
      setSelectedEquipment(null);
    }
  };

  const handleDocumentDownload = (doc: any) => {
    if (doc && doc.fileUrl) {
      window.open(doc.fileUrl, '_blank');
    }
  };

  const handleDelete = async () => {
    if (!equipmentToDelete || !equipmentToDelete.id) {
      console.error('Equipment ID is missing');
      return;
    }

    try {
      const payload = {
        ids: [equipmentToDelete.id]
      };
      const result = await bulkDeleteRegistry(payload);
      if (result) {
        // Refresh Redux store data
        if (projectId) {
          dispatch(refreshEquipmentData({ projectId }));
        }
        if (onRefresh) {
          onRefresh();
        }
        setIsDeleteModalVisible(false);
        setEquipmentToDelete(null);
      }
    } catch (error) {
      console.error('Failed to delete equipment:', error);
    }
  };

  const handleEditComplete = (type: string) => {
    setIsEditModalOpen(false);
    setEquipmentToEdit(null);
    // Refresh Redux store data
    if (projectId) {
      dispatch(refreshEquipmentData({ projectId }));
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  // Handle document deletion
  const handleDeleteDocument = (doc: any) => {
    setDocumentToDelete(doc);
    setIsDocumentDeleteModalOpen(true);
  };

  const handleCancelDocumentDelete = () => {
    setIsDocumentDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDocumentDelete = async () => {
    if (!documentToDelete || !selectedEquipment) return;

    setDeleteDocumentLoading(true);
    try {
      // Get docId from the document - try multiple possible field names
      const docId = documentToDelete.docId || documentToDelete.doc_id || documentToDelete.id;
      
      if (!docId) {
        customNotification.error('Failed to delete document', 'Document ID not found');
        setDeleteDocumentLoading(false);
        return;
      }

      const apiPayload = {
        docIds: [Number(docId)]
      };

      // Call delete API
      const deleteResponse = await deleteRegistryDocumentsByDocId(apiPayload);

      if (deleteResponse) {
        // Refresh Redux store data
        if (projectId) {
          dispatch(refreshEquipmentData({ projectId }));
        }
        // if (onRefresh) {
        //   onRefresh();
        // }
        // Close modals
        setIsDocumentDeleteModalOpen(false);
        setIsDownloadModalOpen(false);
        setSelectedEquipment(null);
        setDocumentToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      customNotification.error('Failed to delete document', 'An error occurred while deleting the document.');
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  // Helper function to extract documents from nested structure
  const extractDocuments = (documents: any[]): any[] => {
    if (!documents || documents.length === 0) return [];
    
    // Check if documents are in nested structure (with docRef and materials)
    if (documents[0]?.materials && Array.isArray(documents[0].materials)) {
      // Flatten the nested structure
      return documents.flatMap((docGroup: any) => 
        (docGroup.materials || []).map((doc: any) => ({
          ...doc,
          docRef: docGroup.docRef
        }))
      );
    }
    
    // Return as-is if already flat structure
    return documents;
  };

  const transformDocumentsForDownload = (documents: any[]) => {
    if (!documents || documents.length === 0) return [];
    
    // Extract documents from nested structure if needed
    const flatDocuments = extractDocuments(documents);
    
    return flatDocuments.map((doc: any) => ({
      id: doc.registryDocId || doc.id || doc.docId || Math.random().toString(),
      fileName: doc.fileName || doc.file_name,
      fileUrl: doc.fileUrl || doc.file_url,
      uploadDate: doc.uploadDate || doc.uploadedAt || doc.createdAt,
      uploadedBy: doc.uploadedBy || doc.createdBy
    }));
  };


  if (equipmentLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-[13px] text-heritage-grey">Loading equipment...</div>
      </div>
    );
  }

  if (equipmentList.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-page-bg rounded-full flex items-center justify-center mb-4">
            <span className="text-2xl">📦</span>
          </div>
          <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue mb-2">No equipment found</h3>
          <p className="text-[13px] text-heritage-grey max-w-sm">
            No {equipmentType} equipment has been added yet. Click "Add Equipment" to get started.
          </p>
        </div>

        {/* Upload Modal */}
        {selectedEquipment && isUploadModalOpen && (
          <DocumentUploadModal
            isOpen={isUploadModalOpen}
            onClose={() => {
              setIsUploadModalOpen(false);
              setSelectedEquipment(null);
            }}
            onUpload={handleUploadComplete}
            title={`Upload ${equipmentType.toUpperCase()} Equipment Document`}
            acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
            multiple={true}
            maxFiles={100}
            buttonText="Select Document"
            label="Document"
            description={`Upload ${equipmentType} equipment documents (PDF, Excel, DOC, etc...)`}
            documentType={equipmentType.toUpperCase()}
          />
        )}

        {/* Download Modal */}
        {selectedEquipment && selectedEquipment.documents && extractDocuments(selectedEquipment.documents).length > 0 && (
          <DocumentDownloadModal
            isOpen={isDownloadModalOpen}
            onClose={() => {
              setIsDownloadModalOpen(false);
              setSelectedEquipment(null);
            }}
            documents={transformDocumentsForDownload(selectedEquipment.documents)}
            onDownload={handleDocumentDownload}
            onDelete={(doc) => handleDeleteDocument(doc)}
            title={`Download ${equipmentType.toUpperCase()} Equipment Document`}
            canDelete={true}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="min-w-0 space-y-4">
        <EquipmentFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFilterChange={onFilterChange}
          uniqueEquipmentNumbers={uniqueEquipmentNumbers}
          uniqueEquipmentNames={uniqueEquipmentNames}
          uniqueEquipmentTypes={uniqueEquipmentTypes}
          uniqueSerialNumbers={uniqueSerialNumbers}
          hasActiveFilters={hasActiveFilters}
          filteredCount={filteredCount}
          onResetFilters={onResetFilters}
        />

        <Card className="min-w-0">
          <CardContent className="p-3 sm:p-4">
            <div className="min-w-0 overflow-x-auto">
              <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
                <Table containerClassName="">
                  <TableHeader className="sticky top-0 z-20 bg-white">
                    <TableRow>
                  <TableHead className="min-w-[120px]">Equipment type</TableHead>
                  {/* <TableHead>Equipment Number</TableHead> */}
                  <TableHead>Serial Number</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Calibration Date</TableHead>
                  <TableHead>Certificate Number</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead>Calibration Company</TableHead>
                  <TableHead>Calibration Status</TableHead>
                  <TableHead>Comments</TableHead>
                  <TableHead className="text-center w-[80px]">Documents</TableHead>
                  <TableHead className="text-center w-[80px] sticky right-0 z-10 bg-white">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEquipment.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center text-[13px] text-heritage-grey py-8">
                      No equipment found matching your filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEquipment.map((equipment, index) => {
                  const extractedDocs = extractDocuments(equipment.documents || []);
                  const docCount = extractedDocs.length;
                  
                  return (
                    <TableRow key={equipment.id || index}>
                      <TableCell>{equipment.equipmentName || equipment.equipment_name}</TableCell>
                      {/* <TableCell>{equipment.equipmentNumber || equipment.equipment_number}</TableCell> */}
                      <TableCell>{equipment.serial_number || '-'}</TableCell>
                      <TableCell>{equipment.manufacturer || '-'}</TableCell>
                      <TableCell>{equipment.model || '-'}</TableCell>
                      <TableCell>{equipment.location || '-'}</TableCell>
                      <TableCell>{formatDateOnly(equipment.calibration_date) || ''}</TableCell>
                      <TableCell>{equipment.certificate_number || '-'}</TableCell>
                      <TableCell>{formatDateOnly(equipment.expiry_date) || ''}</TableCell>
                      <TableCell>{equipment.calibration_company || '-'}</TableCell>
                      <TableCell>{equipment.calibration_status || '-'}</TableCell>
                      <TableCell>{equipment.notes || '-'}</TableCell>
                      <TableCell className="text-center w-[80px]">
                        <div className={`flex items-center gap-2 ${docCount === 0 ? 'justify-center' : ''}`}>
                          {docCount > 0 && (
                            <Button
                              className="flex items-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                              title="View Documents"
                              variant="outline"
                              onClick={() => handleDocumentClick(equipment)}
                            >
                              <FileText className="h-4 w-4" />
                              {docCount}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    <TableCell className="text-center w-[80px] sticky right-0 z-10 bg-white">
                      <div className="flex items-center gap-2 justify-center">
                        <Button
                          onClick={() => handleEdit(equipment)}
                          className="text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                          title="Edit Equipment"
                          variant="outline"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          className="text-hero-red hover:text-hero-red hover:bg-[rgba(235,28,55,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                          onClick={() => handleDeleteClick(equipment)}
                          title="Delete Equipment"
                          variant="outline"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
                )}
              </TableBody>
            </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Modal */}
      {selectedEquipment && isUploadModalOpen && (
        <DocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setSelectedEquipment(null);
          }}
          onUpload={handleUploadComplete}
          title={`Upload ${equipmentType.toUpperCase()} Equipment Document`}
          acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
          multiple={true}
          maxFiles={100}
          buttonText="Select Document"
          label="Document"
          description={`Upload ${equipmentType} equipment documents (PDF, Excel, DOC, etc...)`}
          documentType={equipmentType.toUpperCase()}
        />
      )}

      {/* Download Modal */}
      {selectedEquipment && selectedEquipment.documents && extractDocuments(selectedEquipment.documents).length > 0 && (
        <DocumentDownloadModal
          isOpen={isDownloadModalOpen}
          onClose={() => {
            setIsDownloadModalOpen(false);
            setSelectedEquipment(null);
          }}
          documents={transformDocumentsForDownload(selectedEquipment.documents)}
          onDownload={handleDocumentDownload}
          onDelete={(doc) => handleDeleteDocument(doc)}
          title={`Download ${equipmentType.toUpperCase()} Equipment Document`}
          canDelete={true}
        />
      )}

      {/* Edit Modal */}
      {equipmentToEdit && (
        <AddEquipmentModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEquipmentToEdit(null);
          }}
          onAdd={handleEditComplete}
          equipmentType={equipmentType}
          project={project}
          equipmentToEdit={equipmentToEdit}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationDialog
        isOpen={isDeleteModalVisible}
        onClose={() => {
          setIsDeleteModalVisible(false);
          setEquipmentToDelete(null);
        }}
        onConfirm={handleDelete}
        itemName={equipmentToDelete ? (equipmentToDelete.equipmentName || equipmentToDelete.equipment_name) : ''}
        itemType="equipment"
      />

      {/* Document Delete Confirmation Modal */}
      <DeleteConfirmationDialog
        isOpen={isDocumentDeleteModalOpen}
        onClose={handleCancelDocumentDelete}
        onConfirm={handleConfirmDocumentDelete}
        isLoading={deleteDocumentLoading}
        itemToDelete={documentToDelete}
        itemType="Equipment Document"
        itemName={documentToDelete?.fileName || documentToDelete?.file_name || ''}
        title={`Delete ${equipmentType.toUpperCase()} Equipment Document`}
      />
    </>
  );
};

